import { createItemTaxonomy } from "common/helpers/taxonomy-helper";
import { UserUseCase } from "lib/models/userusecase-models";
import { CatalogicalService } from "lib/services/catalogical-service";
import { CSRFService } from "lib/services/csrf-service";
import { UserInsightsService } from "lib/services/user-insights-service";
import { useEffect, useMemo, useState } from "react";

interface UseUseCaseDetailResult {
  useCase: UserUseCase | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Fetches a single use case by its slug (path after atn::) and records a view.
 */
export function useUseCaseDetail(slug: string): UseUseCaseDetailResult {
  const [useCase, setUseCase] = useState<UserUseCase | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const service = useMemo(() => new CatalogicalService<UserUseCase>("userusecase"), []);
  const insightsService = useMemo(() => new UserInsightsService(), []);
  const csrfService = useMemo(() => new CSRFService(), []);

  useEffect(() => {
    if (!slug) return;

    const taxonomyId = createItemTaxonomy(`atn::${slug}`)?.toString() ?? `atn::${slug}`;
    setIsLoading(true);

    service
      .getItems({
        filters: { taxonomyId: [taxonomyId] },
        pageSize: 1,
      })
      .then((response) => {
        const item = response?.data?.[0] ?? null;
        setUseCase(item);
        if (item) {
          csrfService
            .getToken()
            .then((token) => insightsService.recordView(taxonomyId, token))
            .catch(() => {});
        }
      })
      .catch((err) => setError(err))
      .finally(() => setIsLoading(false));
  }, [slug]);

  return { useCase, isLoading, error };
}
